import { formatDate, formatDateTime } from '../../utils/format';
// Pure helpers for the dashboards. No React, no network, so they are easy to test.

export const STAGES = [
  {
    key: 'draft',
    label: 'Draft',
    color: '#adb5bd',
    link: '/apps-procurement/view',
  },
  {
    key: 'hps',
    label: 'HPS Request',
    color: '#299cdb',
    link: '/apps-hps/view',
  },
  {
    key: 'tender',
    label: 'Tender',
    color: '#405189',
    link: '/apps-tenderopening/view',
  },
  {
    key: 'evaluation',
    label: 'Evaluation',
    color: '#f7b84b',
    link: '/apps-tenderevaluation/view',
  },
  {
    key: 'awarded',
    label: 'Awarded',
    color: '#0ab39c',
    link: '/apps-awarding/view',
  },
];

const STAGE_BY_KEY = Object.fromEntries(STAGES.map(s => [s.key, s]));

export const stageLabel = key => STAGE_BY_KEY[key]?.label || key || '-';

const STAGE_BADGE = {
  draft: 'secondary',
  hps: 'info',
  tender: 'primary',
  evaluation: 'warning',
  awarded: 'success',
};

export const stageBadgeColor = key => STAGE_BADGE[key] || 'light';

// Alert copy lives here (not in the API) so wording stays in one English place.
const plural = (n, one, many) => (n === 1 ? one : many);

const ALERT_COPY = {
  'tender-opening-overdue': {
    title: n =>
      `${n} ${plural(n, 'tender is', 'tenders are')} past the submission deadline`,
    hint: 'Open the tender and record the opening result.',
    action: 'Open tenders',
  },
  'tender-deadline-soon': {
    title: n =>
      `${n} ${plural(n, 'tender closes', 'tenders close')} within 3 days`,
    hint: 'Check that the invited suppliers are ready to submit.',
    action: 'Review tenders',
  },
  'hps-stale': {
    title: n =>
      `${n} HPS ${plural(n, 'request has', 'requests have')} waited over 7 days`,
    hint: 'Follow up with the HPS preparer.',
    action: 'Open HPS',
  },
  'evaluation-ready': {
    title: n =>
      `${n} ${plural(n, 'procurement is', 'procurements are')} ready for evaluation`,
    hint: 'Start the evaluation to keep the schedule on track.',
    action: 'Start evaluation',
  },
  'awarding-open': {
    title: n => `${n} awarding ${plural(n, 'is', 'are')} not confirmed yet`,
    hint: 'Confirm the winner to close the procurement.',
    action: 'Open awarding',
  },
  'hps-requested': {
    title: n =>
      `${n} HPS ${plural(n, 'request is', 'requests are')} waiting to be submitted`,
    hint: 'Follow up with the HPS preparer.',
    action: 'Open HPS',
  },
  'draft-stale': {
    title: n =>
      `${n} ${plural(n, 'draft has', 'drafts have')} been idle for over 14 days`,
    hint: 'Complete or remove stale drafts.',
    action: 'Open drafts',
  },
  'mitra-missed': {
    title: n =>
      `${n} ${plural(n, 'offer was', 'offers were')} submitted late or missed the deadline`,
    hint: 'Review these offers; late offers may be rejected.',
    action: 'View offers',
  },
  'mitra-deadline-soon': {
    title: n =>
      `${n} ${plural(n, 'deadline falls', 'deadlines fall')} within 3 days`,
    hint: 'Submit your response before the deadline.',
    action: 'Go to offers',
  },
  'mitra-awaiting-response': {
    title: n =>
      `${n} ${plural(n, 'invitation is', 'invitations are')} waiting for your response`,
    hint: 'Accept or decline so Pertamina can plan the tender.',
    action: 'Respond',
  },
  'mitra-draft-offers': {
    title: n => `${n} ${plural(n, 'offer is', 'offers are')} still in draft`,
    hint: 'Finish and submit before the deadline.',
    action: 'Continue offer',
  },
};

const SEVERITY_RANK = { danger: 0, warning: 1, info: 2 };

// ---- Fallback for an API without /dashboard/overview (older deployment) ----
// Only figures the old endpoints give exactly; anything else stays null so the
// page shows "not available" instead of a guessed number.

const num = value => Number(value) || 0;

/** From GET dashboard/procurement (dictionary) + GET dashboard/get-by-product. */
export const overviewFromLegacy = (counts = {}, products = []) => {
  const grouped = (Array.isArray(products) ? products : []).reduce(
    (acc, item) => {
      const name = item.productName?.[0] || 'Unknown';
      acc[name] = (acc[name] || 0) + num(item.totalProcurements);
      return acc;
    },
    {}
  );
  const ready = num(counts.TotalTenderEvaluationReadyForEvaluation);
  const hpsRequested = num(counts.TotalHpsStatusRequested);
  const alerts = [
    {
      key: 'evaluation-ready',
      severity: 'info',
      count: ready,
      link: '/apps-tenderevaluation/view',
    },
    {
      key: 'hps-requested',
      severity: 'info',
      count: hpsRequested,
      link: '/apps-hps/view',
    },
  ].filter(a => a.count > 0);

  return {
    total: num(counts.TotalProcurements),
    draft: num(counts.TotalDraftProcurements),
    inProgress: null,
    awarded: null,
    evaluationQueue:
      num(counts.TotalTenderEvaluationAwaitingEvaluation) + ready,
    underEvaluation: num(counts.TotalTenderEvaluationUnderEvaluation),
    pipeline: [],
    byProduct: Object.entries(grouped)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count),
    alerts,
    recentProcurements: [],
    recentActivities: [],
  };
};

/** From GET dashboard/get-mitra ([{ key, value }]). */
export const mitraOverviewFromLegacy = (list = []) => {
  const map = Object.fromEntries(
    (Array.isArray(list) ? list : []).map(item => [item.key, num(item.value)])
  );
  const missed = map.TotalSubmissionDeadlineMissed || 0;
  return {
    total: map.TotalProcurements || 0,
    submitted: map.TotalSubmitted || 0,
    opening: map.TotalOpening || 0,
    pastDeadline: map.TotalPastDeadline || 0,
    closing: map.TotalClosing || 0,
    missed,
    awaitingResponse: null,
    draftOffers: null,
    deadlineSoon: null,
    alerts: missed
      ? [
          {
            key: 'mitra-missed',
            severity: 'danger',
            count: missed,
            link: '/apps-offer/view',
          },
        ]
      : [],
    upcomingDeadlines: [],
    recentActivities: [],
  };
};

export const loadLegacyOverview = async getJson => {
  const [counts, products] = await Promise.all([
    getJson('procurement'),
    getJson('get-by-product'),
  ]);
  return overviewFromLegacy(counts, products);
};

export const loadLegacyMitraOverview = async getJson =>
  mitraOverviewFromLegacy(await getJson('get-mitra'));

/** Adds copy to API alerts and sorts them most urgent first. Unknown keys are dropped. */
export const decorateAlerts = (alerts = []) =>
  alerts
    .filter(a => ALERT_COPY[a.key] && Number(a.count) > 0)
    .map(a => ({
      ...a,
      title: ALERT_COPY[a.key].title(a.count),
      hint: ALERT_COPY[a.key].hint,
      action: ALERT_COPY[a.key].action,
    }))
    .sort(
      (a, b) =>
        (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3) ||
        b.count - a.count
    );

/**
 * Month-over-month change. `direction` is 'up' | 'down' | 'flat' | 'new' | 'none'.
 * 'new' = there was nothing last month, so a percentage would be meaningless.
 */
export const computeTrend = (current, previous) => {
  const cur = Number(current) || 0;
  const prev = Number(previous) || 0;
  if (cur === 0 && prev === 0)
    return { direction: 'none', percent: 0, delta: 0 };
  if (prev === 0) return { direction: 'new', percent: null, delta: cur };
  const delta = cur - prev;
  if (delta === 0) return { direction: 'flat', percent: 0, delta: 0 };
  return {
    direction: delta > 0 ? 'up' : 'down',
    percent: Math.round((Math.abs(delta) / prev) * 100),
    delta,
  };
};

export const percentOf = (part, total) =>
  total > 0 ? Math.round(((Number(part) || 0) / total) * 100) : 0;

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "5 min ago", "3 h ago", "2 days ago"; falls back to a short date after a week. */
export const timeAgo = (value, now = Date.now()) => {
  if (!value) return '-';
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return '-';
  const diff = Math.max(0, now - time);
  if (diff < MINUTE) return 'Just now';
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)} min ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)} h ago`;
  if (diff < 7 * DAY) {
    const days = Math.floor(diff / DAY);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }
  return formatDate(time);
};

/** Time left until `value`: { label, tone } where tone is danger | warning | muted. */
export const deadlineInfo = (value, now = Date.now()) => {
  if (!value) return { label: '-', tone: 'muted' };
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return { label: '-', tone: 'muted' };
  const diff = time - now;
  if (diff < 0) return { label: 'Passed', tone: 'danger' };
  if (diff < DAY)
    return {
      label: `${Math.max(1, Math.floor(diff / HOUR))} h left`,
      tone: 'danger',
    };
  const days = Math.floor(diff / DAY);
  return {
    label: `${days} ${days === 1 ? 'day' : 'days'} left`,
    tone: days <= 3 ? 'warning' : 'muted',
  };
};

export { formatDateTime };

export const greeting = (date = new Date()) => {
  const hour = date.getHours();
  if (hour < 11) return 'Good morning';
  if (hour < 15) return 'Good afternoon';
  if (hour < 19) return 'Good evening';
  return 'Good night';
};
